import { useState, type FormEvent } from 'react';

import { Button, Card, Dropdown, Field, Input, MonoLabel } from '@/design-system';
import { IMPERSONATION_MAX_MINUTES, PLATFORM_REASON_MIN_LENGTH } from '@/entities/tenant';
import { MEMBERSHIP_STATUS_LABELS, type PlatformUser } from '@/entities/user';
import { ROLE_LABELS } from '@/shared/api';

import { adminCopy } from '../copy';

export interface ImpersonationFormProps {
    tenantUuid: string;
    tenantName: string;
    /** Personen met een lidmaatschap van *deze* organisatie. */
    users: readonly PlatformUser[];
    /** Vooraf gekozen persoon, bijvoorbeeld vanuit het detail van die persoon. */
    initialUserUuid?: string;
    onStart: (input: { user: PlatformUser; reason: string; minutes: number }) => void;
    starting?: boolean;
    loadingUsers?: boolean;
    error?: string | null;
}

/**
 * Meekijken als een persoon **bij een bepaald bedrijf**.
 *
 * Een keurmeester kan bij meerdere keuringsbedrijven horen, dus "meekijken als
 * Ruud" bestaat niet: je kijkt mee als Ruud *bij Keurhuis Voorbeeld*. De organisatie staat
 * al vast (je komt hier vanuit haar detail), daarna kies je de persoon. Alleen
 * een actief lidmaatschap kan: de API weigert de rest.
 *
 * De reden is verplicht en wordt gelogd. Zodra de sessie loopt staat er een
 * balk boven het scherm die niet weg te klikken is.
 */
export function ImpersonationForm({
    tenantUuid,
    tenantName,
    users,
    initialUserUuid = '',
    onStart,
    starting = false,
    loadingUsers = false,
    error = null,
}: ImpersonationFormProps) {
    const [userUuid, setUserUuid] = useState(initialUserUuid);
    const [reason, setReason] = useState('');
    const [minutes, setMinutes] = useState('30');

    const candidates = users.filter(
        (user) =>
            !user.isPlatformAdmin &&
            user.memberships.some(
                (entry) => entry.tenantUuid === tenantUuid && entry.status === 'active',
            ),
    );
    const selected = candidates.find((user) => user.uuid === userUuid) ?? null;
    const reasonValid = reason.trim().length >= PLATFORM_REASON_MIN_LENGTH;
    const minutesValue = Number(minutes);
    const minutesValid =
        Number.isInteger(minutesValue) &&
        minutesValue >= 1 &&
        minutesValue <= IMPERSONATION_MAX_MINUTES;

    function submit(event: FormEvent) {
        event.preventDefault();

        if (!selected || !reasonValid || !minutesValid) {
            return;
        }

        onStart({ user: selected, reason: reason.trim(), minutes: minutesValue });
        setReason('');
    }

    return (
        <Card padding="md" as="section" aria-labelledby="impersonate-title">
            <h2
                id="impersonate-title"
                className="m-0 font-[family-name:var(--font-display)] text-[15px] leading-[1.3] font-semibold text-[var(--text-primary)]"
            >
                {adminCopy.impersonateTitle}
            </h2>
            <p className="m-0 mt-1 text-[12px] leading-[1.5] text-[var(--text-tertiary-safe)]">
                {adminCopy.impersonateIntro(tenantName)}
            </p>

            <form onSubmit={submit} className="mt-3 grid gap-[10px]">
                <Field
                    label={adminCopy.impersonateTenant}
                    size="sm"
                    description={adminCopy.impersonateTenantHint}
                >
                    <Input value={tenantName} readOnly />
                </Field>

                <Field label={adminCopy.impersonateUser} size="sm">
                    <Dropdown
                        value={selected ? userUuid : ''}
                        disabled={loadingUsers}
                        placeholder={
                            loadingUsers
                                ? adminCopy.impersonateUsersLoading
                                : adminCopy.impersonateUserPick
                        }
                        options={candidates.map((user) => {
                            const membership = user.memberships.find(
                                (entry) => entry.tenantUuid === tenantUuid,
                            );

                            return {
                                value: user.uuid,
                                label: user.name,
                                hint: membership
                                    ? adminCopy.impersonateOptionHint(
                                          membership.role
                                              ? ROLE_LABELS[membership.role]
                                              : adminCopy.unknownRole,
                                          MEMBERSHIP_STATUS_LABELS[membership.status].toLowerCase(),
                                      )
                                    : undefined,
                            };
                        })}
                        onChange={setUserUuid}
                    />
                </Field>

                <Field
                    label={adminCopy.reason}
                    size="sm"
                    description={adminCopy.impersonateReasonHint}
                    error={
                        reason !== '' && !reasonValid
                            ? adminCopy.reasonTooShort(PLATFORM_REASON_MIN_LENGTH)
                            : undefined
                    }
                >
                    <Input
                        value={reason}
                        placeholder={adminCopy.impersonateReasonPlaceholder}
                        onChange={(event) => setReason(event.target.value)}
                    />
                </Field>

                <Field
                    label={adminCopy.impersonateMinutes}
                    size="sm"
                    description={adminCopy.impersonateMinutesHint}
                    className="max-w-[200px]"
                >
                    <Input
                        mono
                        inputMode="numeric"
                        value={minutes}
                        onChange={(event) => setMinutes(event.target.value)}
                    />
                </Field>

                <div className="flex flex-wrap items-center gap-3">
                    <Button
                        type="submit"
                        variant="warn"
                        size="sm"
                        disabled={starting || !selected || !reasonValid || !minutesValid}
                    >
                        {adminCopy.impersonateStart}
                    </Button>
                    <MonoLabel size={10} tone="muted">
                        {adminCopy.impersonateLogged}
                    </MonoLabel>
                </div>
            </form>

            {error ? (
                <p role="alert" className="m-0 mt-3 text-[12px] leading-[1.4] text-[var(--danger)]">
                    {error}
                </p>
            ) : null}
        </Card>
    );
}
